"use client";

import { useState } from "react";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import SourceCard from "@/app/(components)/generics/cards/SourceCard/SourceCard";
import IconCheck from "@/app/(components)/generics/Icons/IconCheck";
import { Input, TextInputType } from "@/app/(components)/generics/inputs/default/Input/Input";
import CircleLoader from "@/app/(components)/generics/loaders/CircleLoader/CircleLoader";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import { useCanRunPaidActions } from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { createProjectOffer } from "@/app/actions/offer/create/action";
import { DataValidationError } from "@/app/errors/DataValidationError";
import { ModalStore } from "@/app/stores/ModalStore";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { TKeyClientAvatar } from "@/app/translations/keys/TKeyClientAvatar";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { ProjectOfferData } from "@/app/types/client-avatar/UserClientAvatar";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { UStyles } from "@/app/utils/UStyles";
import { StepState } from "./ExtractAvatar.logic";
import styles from "./ExtractAvatar.module.css";

type Props = DefaultProps & {
  isButton: boolean;
  atLeastOneAnalysedCall: boolean | undefined;
  offers: ProjectOfferData[] | undefined;
  onOfferCreated: () => void;
  onExtract: () => void;
  isAnalyzing?: boolean;
};

export default function ExtractAvatar(props: Props) {
  const T = useT();
  const {
    params,
    isButton,
    atLeastOneAnalysedCall,
    offers,
    onOfferCreated,
    onExtract,
    isAnalyzing,
  } = props;

  const hasOffer = offers !== undefined && offers.length > 0;

  const user = useAuthUser();
  const project = ProjectStore((state) => state.currentProject);
  const openSignUp = () => ModalStore.getState().openSignUp(params);

  const [offerData, setOfferData] = useState({ name: "", description: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameError, setNameError] = useState("");

  const canRunPaidActions = useCanRunPaidActions();
  if (!canRunPaidActions) {
    return null;
  }

  const handleCreateOffer = async () => {
    if (!offerData.name.trim() || !project?.id) return;
    setIsSubmitting(true);
    setNameError("");
    try {
      await createProjectOffer(project.id, {
        name: offerData.name.trim(),
        problem: offerData.description.trim() || undefined,
      });
      onOfferCreated();
    } catch (err: unknown) {
      if (err instanceof DataValidationError) {
        setNameError(T(TKey.ERROR_TOO_LONG_VALUE) as string);
      } else {
        setNameError(T(TKey.ERROR) as string);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const button = (
    <Button
      min={true}
      isLink={false}
      type={ButtonType.PRIMARY}
      text={
        <span className={isButton ? styles.hideTextOnMobile : ""}>
          {T(TKey.CLIENT_AVATAR_EXTRACT_BUTTON) as string}
        </span>
      }
      beforeIcon={user && isAnalyzing ? <CircleLoader size={16} color="currentColor" /> : undefined}
      onClick={!user ? openSignUp : onExtract}
    />
  );

  if (isButton) return button;

  const renderStep = (num: number, labelKey: TKey, state: StepState) => (
    <div
      className={UStyles.classNames(
        styles.step,
        state === StepState.DONE && styles.stepDone,
        state === StepState.LOCKED && styles.stepLocked,
      )}
    >
      <span
        className={UStyles.classNames(
          styles.stepNumber,
          state === StepState.DONE && styles.stepNumberDone,
          state === StepState.LOCKED && styles.stepNumberLocked,
        )}
      >
        {state === StepState.DONE ? <IconCheck /> : num}
      </span>
      <span className={styles.stepLabel}>{T(labelKey)}</span>
    </div>
  );

  const cardTitle = T(TKey.CLIENT_AVATAR_EXTRACT_TITLE) as string;
  const cardBadge = T(TKey.CLIENT_AVATAR_EXTRACT_BADGE) as string;

  if (!hasOffer && user) {
    return (
      <SourceCard title={cardTitle} badge={cardBadge}>
        <div className={styles.steps}>
          {renderStep(1, TKeyClientAvatar.CLIENT_AVATAR_EXTRACT_STEP_1, StepState.DONE)}
          {renderStep(2, TKeyClientAvatar.CLIENT_AVATAR_EXTRACT_STEP_2, StepState.ACTIVE)}
          {renderStep(3, TKeyClientAvatar.CLIENT_AVATAR_EXTRACT_STEP_3, StepState.LOCKED)}
        </div>
        <div className={styles.offerForm}>
          <Input
            type={TextInputType.TEXT}
            data={offerData}
            dataKey="name"
            label={T(TKey.OFFER_NAME_LABEL) as string}
            placeholder={T(TKey.OFFER_NAME_PLACEHOLDER) as string}
            onChange={(d) => {
              setOfferData(d);
              setNameError("");
            }}
            errorMessage={nameError}
            charsLimit={{ max: 100 }}
          />
          <Input
            type={TextInputType.TEXTAREA}
            data={offerData}
            dataKey="description"
            label={T(TKey.OFFER_PROBLEM_LABEL) as string}
            placeholder={T(TKey.OFFER_PROBLEM_PLACEHOLDER) as string}
            onChange={setOfferData}
            textareaRows={3}
            charsLimit={{ max: 1000 }}
          />
          <div className={styles.buttonWrapper}>
            <Button
              min={true}
              isLink={false}
              type={ButtonType.PRIMARY}
              text={T(TKey.OFFER_CREATE_BUTTON) as string}
              onClick={handleCreateOffer}
              disabled={!offerData.name.trim() || isSubmitting}
            />
          </div>
        </div>
      </SourceCard>
    );
  }

  if (user && atLeastOneAnalysedCall === true && hasOffer) {
    return (
      <SourceCard
        title={cardTitle}
        badge={cardBadge}
        actions={
          <Button
            min={true}
            isLink={false}
            type={ButtonType.PRIMARY}
            text={T(TKey.CLIENT_AVATAR_EXTRACT_BUTTON) as string}
            onClick={onExtract}
            beforeIcon={isAnalyzing ? <CircleLoader size={16} color="currentColor" /> : undefined}
          />
        }
      >
        <div className={styles.steps}>
          {renderStep(1, TKeyClientAvatar.CLIENT_AVATAR_EXTRACT_STEP_1, StepState.DONE)}
          {renderStep(2, TKeyClientAvatar.CLIENT_AVATAR_EXTRACT_STEP_2, StepState.DONE)}
          {renderStep(3, TKeyClientAvatar.CLIENT_AVATAR_EXTRACT_STEP_3, StepState.ACTIVE)}
        </div>
      </SourceCard>
    );
  }

  return (
    <SourceCard
      title={cardTitle}
      badge={cardBadge}
      description={T(TKey.CLIENT_AVATAR_EXTRACT_DESCRIPTION) as string}
      benefits={[
        T(TKey.CLIENT_AVATAR_EXTRACT_BENEFIT_1) as string,
        T(TKey.CLIENT_AVATAR_EXTRACT_BENEFIT_2) as string,
        T(TKey.CLIENT_AVATAR_EXTRACT_BENEFIT_3) as string,
      ]}
      actions={button}
    />
  );
}
